import axios from 'axios'

import { optimoPublicApi, optimoPrivateApi } from '../../axios/axiosInstance'
import { endpoints } from '../../endpoints'

import type {
    CheckEmailPayload,
    CheckEmailResponse,
    SignupPayload,
    SignupResponse,
    IOrganizationSignupParams,
    IOrganizationSignupResponse,
    ExchangeTokenPayload,
    ExchangeTokenResponse,
    RefreshTokenResponse,
    UserData,
    ImpersonationStatusResponse,
} from './authActions.types'

import { getAuthToken } from '@/utils/auth/tokenManager'

export const checkEmail = async (values: CheckEmailPayload): Promise<CheckEmailResponse> => {
    const response = await optimoPublicApi.post<CheckEmailResponse>(endpoints.auth.check, values)
    return response.data
}

export const signup = async (values: SignupPayload): Promise<SignupResponse> => {
    const response = await optimoPublicApi.post<SignupResponse>(endpoints.auth.signup, values)
    return response.data
}

export const organizationSignup = async (params: IOrganizationSignupParams): Promise<IOrganizationSignupResponse> => {
    const response = await optimoPublicApi.post<IOrganizationSignupResponse>(endpoints.auth.signup, params)
    return response.data
}

export const exchangeMagicToken = async (values: ExchangeTokenPayload): Promise<ExchangeTokenResponse> => {
    const response = await optimoPublicApi.post<ExchangeTokenResponse>(endpoints.auth.exchangeMagicToken, values)
    return response.data
}

export const exchangeImpersonationToken = async (
    values: ExchangeTokenPayload,
): Promise<Pick<ExchangeTokenResponse, 'access_token' | 'expires_at' | 'user'>> => {
    const response = await optimoPublicApi.post<Pick<ExchangeTokenResponse, 'access_token' | 'expires_at' | 'user'>>(
        endpoints.impersonation.exchangeToken,
        values,
    )
    return response.data
}

export const refreshToken = async (): Promise<RefreshTokenResponse> => {
    const response = await optimoPrivateApi.post<RefreshTokenResponse>(endpoints.auth.refresh)
    return response.data
}

export const refreshTokenDirect = async (): Promise<RefreshTokenResponse> => {
    const refreshToken = getAuthToken('refresh')

    if (!refreshToken) {
        throw new Error('No refresh token available')
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/optimo/api/v1'

    const response = await axios.post<RefreshTokenResponse>(
        `${apiUrl}${endpoints.auth.refreshToken}`,
        {},
        {
            headers: {
                'Content-Type': 'application/json',
                'X-Refresh-Token': refreshToken,
            },
            timeout: 10000,
            withCredentials: false,
        },
    )

    return response.data
}

export const logout = async (): Promise<void> => {
    await optimoPrivateApi.post<void>(endpoints.auth.logout)
}

export const logoutAll = async (): Promise<{ detail: string; sessions_terminated: number }> => {
    const response = await optimoPrivateApi.post<{ detail: string; sessions_terminated: number }>(
        endpoints.auth.logoutAll,
    )
    return response.data
}

export const getCurrentUser = async (): Promise<UserData> => {
    const response = await optimoPrivateApi.get<UserData>(endpoints.auth.user)
    return response.data
}

// --- Impersonation helpers -----------------------------------------------------

// ImpersonationStatusResponse is declared in authActions.types.ts to keep
// the shape shared with hooks/components that consume it.

/**
 * Return current impersonation status for the active session.
 * The private API instance attaches the access token automatically.
 */
export const getImpersonationStatus = async (): Promise<ImpersonationStatusResponse> => {
    const response = await optimoPrivateApi.get<ImpersonationStatusResponse>(endpoints.impersonation.status)
    return response.data
}

/**
 * End the current impersonation session on the backend.
 * The server will invalidate the token and create audit logs.
 */
export const endImpersonation = async (): Promise<void> => {
    await optimoPrivateApi.post(endpoints.impersonation.end)
}
